/**
 * A figure that is being proposed: the new value emphasised, the committed one
 * struck directly beneath it.
 *
 * Owns BOTH figures, for a control whose value is a plain label the draft can
 * restyle — the limit slider's. Renders the value unchanged when nothing is
 * staged, so it is safe as that slot's permanent renderer.
 *
 * See `StagedWas` where the control already shows the new value, and
 * `StagedTransition` where it shows neither.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useDraft } from '../DraftContext';
import type { FieldPath } from '../types';
import { CS_PROPOSED_TEXT, stagedWasSx } from './stagedStyles';

export interface StagedValueProps {
  /** Every address a change to this figure could be staged at. */
  readonly paths: readonly FieldPath[];
  /** The figure as the control itself formats it. */
  readonly formatted: string;
  /**
   * The control's own formatter, applied to the committed figure too, so the
   * pair shares one notation. Pass the control's function, not a copy of its
   * format string.
   */
  readonly format?: (value: number) => string;
}

const StagedValue = ({ paths, formatted, format }: StagedValueProps) => {
  const { stagedFields } = useDraft();
  const changes = stagedFields.at(paths);

  if (changes.length === 0) {
    return <span style={{ width: '1.5rem' }}>{formatted}</span>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        lineHeight: 1.1,
      }}
    >
      <Box
        component="span"
        sx={{
          color: CS_PROPOSED_TEXT,
          fontWeight: 700,
        }}
      >
        {formatted}
      </Box>
      {changes.map((change) => (
        <Typography
          key={change.id}
          variant="caption"
          color="text.disabled"
          sx={stagedWasSx}
        >
          {format && typeof change.fromValue === 'number'
            ? format(change.fromValue)
            : change.fromLabel}
        </Typography>
      ))}
    </Box>
  );
};

export default StagedValue;
